import { observer } from 'mobx-react-lite'
import { RawButton } from '@/components/Button'
import { StyledLink } from '@/components/Link'
import { links } from '@/utils/link'
import type ConsultationManagerViewModel from '../../ConsultationManagerViewModel'
import styles from './ScheduleCallComposerAction.module.css'
import {
  ScheduleCallComposerCallout,
  ScheduleCallComposerIconButton,
} from './index'

type ComposerState =
  | { type: 'joinCall'; label: string }
  | { type: 'callCompleted' }
  | { type: 'scheduleCall' }
  | { type: 'awaitingClaim' }
  | null

function getComposerState(
  consultationManagerVm: ConsultationManagerViewModel,
): ComposerState {
  const consultation = consultationManagerVm.consultation
  if (!consultation) {
    return null
  }

  const matchedState = consultation.match<
    | { type: 'joinCall'; label: string }
    | { type: 'callCompleted' }
    | { type: 'awaitingClaim' }
    | null
  >({
    awaitingPickup: () =>
      consultationManagerVm.isAwaitingProClaim
        ? { type: 'awaitingClaim' }
        : null,
    booked: (stage) => ({
      type: 'joinCall',
      label: `Join your call on ${stage.bookedTime.toStartingDayString()} at ${stage.bookedTime.toStartingHourString()}`,
    }),
    dayOfCall: (stage) => ({
      type: 'joinCall',
      label: `Join your call today at ${stage.bookedTime.toStartingHourString()}`,
    }),
    inProgress: () => ({
      type: 'joinCall',
      label: 'Join your call now',
    }),
    callCompleted: () => ({
      type: 'callCompleted',
    }),
    default: () => null,
  })

  if (matchedState) {
    return matchedState
  }

  if (
    consultationManagerVm.isSettingAvailabilityAllowed &&
    !consultationManagerVm.consultationSchedulerVm.isAvailabilitySet
  ) {
    return { type: 'scheduleCall' }
  }

  return null
}

interface ScheduleCallCalloutContainerProps {
  consultationManagerVm: ConsultationManagerViewModel
}

export const ScheduleCallCalloutContainer = observer(
  function ScheduleCallCalloutContainer({
    consultationManagerVm,
  }: ScheduleCallCalloutContainerProps) {
    const state = getComposerState(consultationManagerVm)
    if (!state) {
      return null
    }

    if (state.type === 'joinCall') {
      return (
        <ScheduleCallComposerCallout>
          <RawButton
            className={styles.calloutAction}
            onPress={() => consultationManagerVm.joinRoom()}
          >
            {state.label}
          </RawButton>
          {' · '}
          <StyledLink to={links.jobDetails(consultationManagerVm.job.id)}>
            View appointment
          </StyledLink>
        </ScheduleCallComposerCallout>
      )
    }

    if (state.type === 'callCompleted') {
      return (
        <ScheduleCallComposerCallout>
          If you have any questions or want to set up another consultation,
          please contact your Relationship Manager.
        </ScheduleCallComposerCallout>
      )
    }

    if (state.type === 'awaitingClaim') {
      return (
        <ScheduleCallComposerCallout>
          Your availability selection will be enabled once a tax professional
          has been assigned to your job.
        </ScheduleCallComposerCallout>
      )
    }

    return (
      <ScheduleCallComposerCallout
        onPress={() => consultationManagerVm.showSetAvailabilityDialog()}
      >
        Please set your availability for your consultation. If you have any
        questions about how to join, please contact your Relationship Manager.
      </ScheduleCallComposerCallout>
    )
  },
)

interface ScheduleCallLeadingActionContainerProps {
  consultationManagerVm: ConsultationManagerViewModel
}

export const ScheduleCallLeadingActionContainer = observer(
  function ScheduleCallLeadingActionContainer({
    consultationManagerVm,
  }: ScheduleCallLeadingActionContainerProps) {
    const state = getComposerState(consultationManagerVm)
    if (!state) {
      return null
    }

    if (state.type === 'joinCall') {
      return (
        <ScheduleCallComposerIconButton
          iconName="call-start-call"
          iconLabel="Join call"
          onPress={() => consultationManagerVm.joinRoom()}
          isDisabled={consultationManagerVm.joinRoom.pending}
          testId="join-call-composer-button"
        />
      )
    }

    if (state.type === 'scheduleCall') {
      return (
        <ScheduleCallComposerIconButton
          iconName="calendar"
          iconLabel="Schedule call"
          onPress={() => consultationManagerVm.showSetAvailabilityDialog()}
          testId="schedule-call-composer-button"
        />
      )
    }

    return null
  },
)
